import React, { useState, useEffect, useRef} from 'react';
import language from '../../../utils/LanguageSettings';
import '../../../css/business.css';
import RenderSnA from '../../functions/renderSnA';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import gfm from 'remark-gfm';
import { fetchBusiness } from '../../functions/fetchBusiness';
import BusinessBanner from './BusinessBanner';
import BusinessTncModal from './BusinessTncModal';
import { SOURCE_URL } from '../../../config';
import closeImage from '../../../images/icon/icon-close.svg';
import { useInView } from 'react-intersection-observer';
import 'animate.css';
import { animationClass, animationStyle } from '../../../utils/FadeInEffect';
import BusinessFullScreenTemplate from './BusinessFullScreenTemplate';
import BusinessSplitTemplate from './BusinessSplitTemplate';

interface BusinessProps {

}

interface UseInViewReturn {
  ref: (node?: Element | null | undefined) => void;
  inView: boolean;
  entry?: IntersectionObserverEntry;
  reset?: () => void;
}

const Business: React.FC<BusinessProps> = () => {
    const { ref: ref1, inView: inView1 } = useInView({
        threshold: 0.1,
        triggerOnce: true,
    });
    const { ref: ref2, inView: inView2 } = useInView({
        threshold: 0.1,
        triggerOnce: true,
    });
    const modalInViewResult = useInView({
        threshold: 0.01,
        triggerOnce: false,
        initialInView: false,
    }) as UseInViewReturn;
    const { ref: modalRef3 } = modalInViewResult;
    
     const resetInView3 = modalInViewResult.reset || (() => {
        const modalElement = document.querySelector('.content-wrapper.pack-modal-style2');
        if (modalElement) {
            const tempObserver = new IntersectionObserver(() => {}, { threshold: 0.01 });
            tempObserver.unobserve(modalElement);
            tempObserver.disconnect();
        }
    });
    const { ref: ref3, inView: inView3 } = useInView({
        threshold: 0.1,
        triggerOnce: true,
    });
    const [loading, setLoading] = useState<boolean>(true);
    const [banner, setBanner] = useState<any[]>([]);
    const [content, setContent] = useState<any>();
    const [seo, setSeo] = useState<any>(null);
    const [ga, setGa] = useState<any>(null);
    const [isShow, setIsShow] = useState<boolean>(false);
    const [targetItem, setTargetItem] = useState<any>(null);
    const [animateModal, setAnimateModal] = useState<boolean>(false);

    const modalContainerRef = useRef<HTMLDivElement>(null);
    const modalContentRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const fetchData = async () => {
            let data = await fetchBusiness(language);
            if(data){
                setContent(data?.attributes);
                setBanner(data?.attributes?.Banner?.filter((singleItem: any) => singleItem?.Status === "Active"));
                setLoading(false);
                setGa(data?.attributes?.GA || null);
                setSeo(data?.attributes?.SEO || null);
            }
        };
        fetchData();
    }, [language]);

    const setUniformHeight = (selector: any) => {
        const elements = document.querySelectorAll(selector);
        if (!elements.length) return;
        elements.forEach(el => {
            (el as HTMLElement).style.height = 'auto';
        });

        let maxHeight = 0;
        elements.forEach(el => {
            maxHeight = Math.max(maxHeight, (el as HTMLElement).offsetHeight);
        });

        elements.forEach(el => {
            (el as HTMLElement).style.height = `${maxHeight}px`;
        });
    };

    const adjustPointListingStyle = () => {
        const pointLists = document.querySelectorAll('.content-wrapper .points');
        pointLists.forEach(list => {
            const liCount = list.querySelectorAll('li').length;
            const ul = list.querySelector('ul');
            if (liCount < 4 && ul) {
                ul.classList.add('count-1');
            }
        });
    };

    const resetStyles = () => {
        document.querySelectorAll('.tabbed-content .business-tab .title h2').forEach(el => {
            (el as HTMLElement).style.height = 'auto';
        });
        document.querySelectorAll('.tabbed-content .business-tab .title p').forEach(el => {
            (el as HTMLElement).style.height = 'auto';
        });
        document.querySelectorAll('.content-wrapper .points ul.count-1').forEach(ul => {
            ul.classList.remove('count-1');
        });
    };

    useEffect(() => {
        if (!content) return;
        
        adjustPointListingStyle();
        setUniformHeight('.tabbed-content .business-tab .title h2');
        setUniformHeight('.tabbed-content .business-tab .title p');

        return () => resetStyles();
    }, [content, language]);

    useEffect(() => {
        const handleResize = () => {
            resetStyles();
            adjustPointListingStyle();
            setUniformHeight('.tabbed-content .business-tab .title h2');
            setUniformHeight('.tabbed-content .business-tab .title p');
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [content, language]);

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (modalContainerRef.current && !modalContentRef.current?.contains(e.target as Node)) {
                closeModel();
            }
        };

        if (isShow) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isShow]);

    const handleTabbedContent = (tabbedContent : any) => {
        return Array.isArray(tabbedContent) && tabbedContent.map((item,index) =>(
            <div className="tab-item business-tab" data-target={"content-"+index} key={index}>
                <div className="position-relative tab-title">
                    <div className="object-item bg-image" data-imgposition="center center">
                        <img className="w-100 img-fit object-desktop-image lazyloaded" src={SOURCE_URL + item?.ImageThumbnail?.data?.attributes?.url} />
                    </div>
                    <div className="object-item title">
                        <ReactMarkdown remarkPlugins={[gfm]} rehypePlugins={[rehypeRaw]}>{item?.TabIntroduction}</ReactMarkdown>
                    </div>
                    {item?.TabDetails && <div className="object-item cta-button">
                        <a style={{cursor:"pointer"}} onClick={() => openDetail(item)} className="link" data-toggle="modal" data-target={"#fnb-modal-"+index}>{language == "en" ? "See Details" :"更多"} <i className="icon-angle-right"></i></a>
                    </div>}
                </div>
            </div>
        ));
    }

    useEffect(() => {
        const body = document.body;
        if (isShow) {
            body.style.overflow = 'hidden';
            body.style.paddingRight = '0';
        } else {
            body.style.overflow = '';
            body.style.paddingRight = '';
        }

        return () => {
            body.style.overflow = '';
            body.style.paddingRight = '';
        };
    }, [isShow]);

    const openDetail = (targetItem: string) => {
        setTargetItem(targetItem);
        setAnimateModal(false);
        resetInView3();
        setTimeout(() => {
            setIsShow(true);
            setTimeout(() => setAnimateModal(true), 150);
            if (modalContainerRef.current) {
                void modalContainerRef.current.offsetHeight;
            }
        }, 50);
        document.body.classList.add("modal-open");
    }

    const closeModel = () => {
        setIsShow(false);
        setAnimateModal(false);
        setTimeout(() => {
            setTargetItem(null);
            resetInView3();
            document.body.classList.remove("modal-open");
        }, 400);
    }
  
    if (loading) {
        return (<>
            <div id="loader">
                <div className="spinner-border"></div>
            </div>
        </>
        );
    }
    return (
      <>
        <RenderSnA seo={seo} ga={ga} />
        <section id='sliding-banner'>
            <div className='flexslider mb-0 white-bg' style={{height: "100%"}}>
                <BusinessBanner element={banner}/>
            </div>
        </section>
        <section id="main-content">
            <div className="introduction-wrapper">
                <div ref={ref1} className={`introduction ${animationClass(inView1, 'up')}`} style={animationStyle(inView1,'up')}>
                    <ReactMarkdown remarkPlugins={[gfm]} rehypePlugins={[rehypeRaw]}>{content?.Introduction}</ReactMarkdown>
                </div>
                <div ref={ref2} className={`row tabbed-content justify-content-center ${animationClass(inView2, 'up')}`} style={animationStyle(inView2,'up')}>
                    {handleTabbedContent(content?.TabbedContent)}
                </div>
            </div>
            <div ref={modalContainerRef} className={`business-modal modal pack-details get-started popup-lg ${isShow ? 'modal-open' : ''}`}>
                <div className="modal-dialog modal-dialog-centered" role="document">
                    <div ref={modalContentRef} className="modal-content">
                        <a style={{cursor:"pointer"}} onClick={closeModel} data-dismiss="modal" className="close-btn text-16"><img src={closeImage} alt="Close icon" className='lazyloaded' />{language == "en" ? " Close" : " 關閉"} </a>
                        <div className="orange-line"></div>
                        <div className="modal-body">
                            {targetItem && (
                                <div className="content-wrapper pack-modal-style2">
                                <div ref={modalRef3} className={`col-lg-4 col-md-4 col-sm-12 offset-lg-1 offset-md-1 left image ${animationClass(animateModal, 'left')}`} style={animationStyle(animateModal,'left')}>
                                    <img className="main-tab-img img-fluid mx-lg-0 mx-auto d-block lazyload" src={SOURCE_URL + targetItem?.TabImage?.data?.attributes?.url} />
                                </div>
                                <div ref={modalRef3} className={`right details ${animationClass(animateModal, 'right')}`} style={animationStyle(animateModal,'right')}>
                                    <ReactMarkdown remarkPlugins={[gfm]} rehypePlugins={[rehypeRaw]}>{targetItem?.TabIntroduction}</ReactMarkdown>
                                    <div className="points">
                                        <ReactMarkdown remarkPlugins={[gfm]} rehypePlugins={[rehypeRaw]}>{targetItem?.TabDetails}</ReactMarkdown>
                                    </div>
                                </div>
                            </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </section>
        {content?.LocationFinderSection[0]?.__component == "business.fullscreen-template" ? 
        <BusinessFullScreenTemplate element={content?.LocationFinderSection[0]} />
        :<BusinessSplitTemplate element={content?.LocationFinderSection[0]} />}
        <section id="contact-info-1">
		    <div ref={ref3} className={`custom-wrapper ${animationClass(inView3, 'up')}`} style={animationStyle(inView3,'up',{paddingBottom:"0"})}>
			    <ReactMarkdown remarkPlugins={[gfm]} rehypePlugins={[rehypeRaw]}>{content?.ContactSection?.Contact}</ReactMarkdown>
			    <div className="icons-wrapper">
                    {Array.isArray(content?.ContactSection?.Icon) && content?.ContactSection?.Icon?.map((item: any,index: number)=>(
                        <div className="item" key={index}>
                            <a className="icon" target="_blank" href={item?.IconURL}>
                                <img className="lazyloaded" src={SOURCE_URL + item?.IconImage?.data?.attributes?.url} />
                            </a>
                            <h4>{item?.IconLabel}</h4>
                        </div>
                    ))}
                </div>
                {/* <div className="text-center or">
				    <span className="barlow-font light-gray my-5 d-inline-block px-3 lg_font_size">{language == "en" ? " or " : " 或 "}</span>
			    </div> */}
	        </div>
        </section>
        <BusinessTncModal />
      </>
    );
};

export default Business;